import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState,
  type ReactNode, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

/* ---------- logo ---------- */
export function Logo() {
  return <span className="logo"><img className="logo-mark" src="/kovio-mark.png" alt="" width={26} height={27} />kovio</span>;
}

/* ---------- toast ---------- */
const ToastCtx = createContext<(msg: string) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState('');
  const [show, setShow] = useState(false);
  const timer = useRef(0);
  const toast = useCallback((m: string) => {
    setMsg(m);
    setShow(true);
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShow(false), 2800);
  }, []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div id="toast" className={show ? 'show' : ''} role="status" aria-live="polite">{msg}</div>
    </ToastCtx.Provider>
  );
}

/* ---------- modal ---------- */
const FOCUSABLE = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  onClose: () => void;
  size?: '' | 'wide' | 'narrow' | 'xwide';
  label?: string;
  children: ReactNode;
  /** Ignore clicks on the backdrop, for example straight after dragging inside the window. */
  ignoreBackdrop?: () => boolean;
}

/** A dialog window over the page. Escape and the backdrop close it; focus stays inside and returns afterwards. */
const TitleId = createContext('m-title');

export function Modal({ onClose, size = '', label, children, ignoreBackdrop }: ModalProps) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useLayoutEffect(() => {
    const ret = document.activeElement;
    const card = cardRef.current;
    const f = card && (card.querySelector<HTMLElement>('[data-autofocus]') || card.querySelector<HTMLElement>('button:not([data-close]):not(:disabled)') || card.querySelector<HTMLElement>('button'));
    f?.focus();
    return () => {
      if (ret instanceof HTMLElement && document.contains(ret)) ret.focus({ preventScroll: true });
    };
  }, []);
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); closeRef.current(); return; }
    if (e.key !== 'Tab' || !cardRef.current) return;
    const f = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  const onBackdrop = (e: ReactMouseEvent) => {
    if (e.target === e.currentTarget && !(ignoreBackdrop && ignoreBackdrop())) closeRef.current();
  };
  return createPortal(
    <div className="modal-layer" onMouseDown={(e) => { (e.currentTarget as HTMLElement).dataset.down = String(e.target === e.currentTarget); }}
      onClick={(e) => { if ((e.currentTarget as HTMLElement).dataset.down === 'true') onBackdrop(e); }} onKeyDown={onKeyDown}>
      <div className={`modal-card ${size}`} role="dialog" aria-modal="true" {...(label ? { 'aria-label': label } : { 'aria-labelledby': titleId })} ref={cardRef}>
        <TitleId.Provider value={titleId}>{children}</TitleId.Provider>
      </div>
    </div>,
    document.body,
  );
}

/** The window's heading, which also names the window for screen readers. */
export function ModalTitle({ children }: { children: ReactNode }) {
  return <h2 id={useContext(TitleId)}>{children}</h2>;
}

export function ModalHead({ title, sub, onClose }: { title: ReactNode; sub?: ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="m-head"><ModalTitle>{title}</ModalTitle><button type="button" className="icon-btn" data-close onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button></div>
      {sub && <p className="m-sub">{sub}</p>}
    </>
  );
}

/* ---------- confirm ---------- */
interface ConfirmOpts { title: string; body: string; yes: string; danger?: boolean }
const ConfirmCtx = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);
export const useConfirm = () => useContext(ConfirmCtx);
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const done = (v: boolean) => { if (state) state.resolve(v); setState(null); };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <Modal onClose={() => done(false)} size="narrow">
          <ModalHead title={state.title} sub={state.body} onClose={() => done(false)} />
          <div className="m-actions">
            <button type="button" className="btn" data-close onClick={() => done(false)}>Cancel</button>
            <button type="button" className={`btn ${state.danger === false ? 'btn-primary' : 'btn-danger solid'}`} data-autofocus onClick={() => done(true)}>{state.yes}</button>
          </div>
        </Modal>
      )}
    </ConfirmCtx.Provider>
  );
}

/* ---------- menus ---------- */
export type MenuItem = { sep: true } | { label: string; icon?: IconName; act: () => void; disabled?: boolean; danger?: boolean };

/** A small list of actions that opens under a button. */
export function Menu({ anchor, items, onClose }: { anchor: HTMLElement; items: MenuItem[]; onClose: (returnFocus: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  // Sits under the button, or above it when there's no room below. Follows the button if the page scrolls.
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = anchor.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight || !anchor.isConnected) { onClose(false); return; }
    const mw = el.offsetWidth, mh = el.offsetHeight;
    let top = r.bottom + 6;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 6);
    setPos({ left: Math.max(8, Math.min(r.right - mw, window.innerWidth - mw - 8)), top });
  }, [anchor, onClose]);
  useLayoutEffect(() => {
    place();
    ref.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);
  useEffect(() => {
    const away = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose(false); };
    const scroll = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) place(); };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, onClose, place]);
  const onKeyDown = (e: ReactKeyboardEvent) => {
    const btns = Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') || []);
    const k = btns.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); btns[(k + 1) % btns.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); btns[(k - 1 + btns.length) % btns.length]?.focus(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); }
    if (e.key === 'Tab') onClose(false);
  };
  return createPortal(
    <div className="menu" role="menu" ref={ref} style={pos} onKeyDown={onKeyDown}>
      {items.map((it, i) => 'sep' in it ? <hr key={i} /> : (
        <button key={i} type="button" role="menuitem" className={it.danger ? 'danger' : undefined} disabled={it.disabled}
          onClick={() => { onClose(false); it.act(); }}>
          <Icon name={it.icon || 'chevR'} size={18} />{it.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}

export function MenuButton({ items, className = 'icon-btn', label, children, ...rest }: { items: () => MenuItem[]; className?: string; label: string; children: ReactNode } & Record<`data-${string}`, string>) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback((ret: boolean) => { setAnchor(null); if (ret) btn.current?.focus({ preventScroll: true }); }, []);
  return (
    <>
      <button type="button" ref={btn} className={className} aria-label={label} aria-haspopup="menu" aria-expanded={!!anchor} {...rest}
        onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}>{children}</button>
      {anchor && <Menu anchor={anchor} items={items()} onClose={close} />}
    </>
  );
}

/* ---------- form bits ---------- */
export function PasswordField({ id, label, autoComplete, hint, labelRow, value, onChange }: {
  id: string; label: string; autoComplete: string; hint?: string; labelRow?: ReactNode; value: string; onChange: (v: string) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="field">
      {labelRow || <label htmlFor={id}>{label}</label>}
      <div className="pw">
        <input className="inp" id={id} name="password" type={show ? 'text' : 'password'} autoComplete={autoComplete} maxLength={128}
          value={value} onChange={(e) => onChange(e.target.value)} {...(hint ? { 'aria-describedby': id + '-hint' } : {})} />
        <button type="button" aria-controls={id} aria-pressed={show} onClick={() => setShow(!show)}>{show ? 'Hide' : 'Show'}</button>
      </div>
      {hint && <p className="hint" id={id + '-hint'} style={{ margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

export function useTitle(title: string) {
  useEffect(() => { document.title = title; }, [title]);
}

/** App screens fill the window and scroll inside; public websites scroll the page. */
export function useAppShell() {
  useEffect(() => {
    document.body.classList.add('app-shell');
    return () => document.body.classList.remove('app-shell');
  }, []);
}
